/**
 * Performance Page
 * Request tracing and performance visualization dashboard
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Clock, AlertTriangle, Activity, Zap, ChevronDown, ChevronRight, ExternalLink, Brain } from 'lucide-react';
import {
  performanceApi,
  type OverviewResponse,
  type RequestListItem,
  type MemoryDecisionDetail,
  type TraceDetailResponse,
} from '@/lib/api/performance';
import { useUsersBatch } from '@/features/image-workflows/hooks/useUsersBatch';

type TimeRange = '1h' | '6h' | '24h' | '7d';

const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
];

function formatMs(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatPercent(rate: number | null): string {
  if (rate === null || rate === undefined) return '-';
  return `${(rate * 100).toFixed(1)}%`;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatScore(score: number | null): string {
  if (score === null || score === undefined) return '-';
  return score.toFixed(2);
}

function formatJsonCell(value: Record<string, unknown> | null | undefined): string {
  if (!value || Object.keys(value).length === 0) return '-';
  return JSON.stringify(value);
}

export type PerformanceStageSegment = {
  label: string;
  ms: number;
  color: string;
};

export function buildPerformanceStageSegments(trace: RequestListItem): PerformanceStageSegment[] {
  const known = [
    { label: 'System Hooks', ms: trace.system_hooks_ms, color: '#9ca3af' },
    { label: 'User Hooks', ms: trace.user_hooks_ms, color: '#8b5cf6' },
    { label: 'LLM', ms: trace.total_llm_ms, color: '#3b82f6' },
    { label: 'Tools', ms: trace.total_tools_ms, color: '#22c55e' },
    { label: 'Final Hooks', ms: trace.final_hooks_ms, color: '#6b7280' },
  ].filter((segment): segment is PerformanceStageSegment => segment.ms !== null && segment.ms > 0);
  const accountedMs = known.reduce((sum, segment) => sum + segment.ms, 0);
  const unattributedMs = Math.max((trace.total_duration_ms ?? 0) - accountedMs, 0);

  return unattributedMs > 0
    ? [...known, { label: 'Unattributed', ms: unattributedMs, color: '#f59e0b' }]
    : known;
}

export function visibleContentWaitMs(trace: RequestListItem): number | null {
  if (trace.first_byte_ms === null || trace.first_content_ms === null) return null;
  return Math.max(trace.first_content_ms - trace.first_byte_ms, 0);
}

function memoryLevelClass(level: string): string {
  switch (level) {
    case 'active_followup':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'soft_mention':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'implicit_tone':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'silent':
      return 'bg-gray-50 text-gray-700 border-gray-200';
    case 'suppress':
      return 'bg-red-50 text-red-800 border-red-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

function MemoryDecisionRows({ decisions }: { decisions: MemoryDecisionDetail[] }) {
  if (!decisions.length) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Brain className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-medium text-gray-700">Memory Decisions</h4>
        <span className="text-xs text-gray-400">({decisions.length})</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="pb-1 pr-4">Level</th>
              <th className="pb-1 pr-4">Memory</th>
              <th className="pb-1 pr-4">Source</th>
              <th className="pb-1 pr-4">Score</th>
              <th className="pb-1 pr-4">Scenario</th>
              <th className="pb-1 pr-4">Reason</th>
              <th className="pb-1 pr-4">Lifecycle</th>
              <th className="pb-1">Update</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((decision) => (
              <tr
                key={`${decision.decision_index}-${decision.memory_id ?? 'unknown'}`}
                className="border-b border-gray-100 align-top"
              >
                <td className="py-2 pr-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-md border text-xs font-medium ${memoryLevelClass(decision.use_level)}`}>
                    {decision.use_level}
                  </span>
                </td>
                <td className="py-2 pr-4">
                  <div className="max-w-[240px]">
                    <div className="truncate text-gray-800" title={decision.content_preview ?? undefined}>
                      {decision.content_preview || '-'}
                    </div>
                    <div className="font-mono text-[11px] text-gray-400 truncate">
                      {decision.memory_id || '-'}
                    </div>
                  </div>
                </td>
                <td className="py-2 pr-4 text-gray-600">
                  <div>{decision.source || '-'}</div>
                  <div className="text-xs text-gray-400">{decision.category || '-'}</div>
                </td>
                <td className="py-2 pr-4 font-mono text-gray-600">{formatScore(decision.score)}</td>
                <td className="py-2 pr-4 text-gray-600">{decision.scenario || '-'}</td>
                <td className="py-2 pr-4 text-gray-700">{decision.reason || '-'}</td>
                <td className="py-2 pr-4">
                  <code className="block max-w-[180px] truncate rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                    {formatJsonCell(decision.lifecycle)}
                  </code>
                </td>
                <td className="py-2">
                  <code className="block max-w-[180px] truncate rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                    {formatJsonCell(decision.lifecycle_update)}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPICards({ kpi }: { kpi: OverviewResponse['kpi'] | undefined }) {
  const cards = [
    {
      label: 'Avg Duration',
      value: formatMs(kpi?.avg_duration_ms ?? null),
      icon: Clock,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'P95 Duration',
      value: formatMs(kpi?.p95_duration_ms ?? null),
      icon: AlertTriangle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Request Count',
      value: kpi?.request_count?.toLocaleString() ?? '-',
      icon: Activity,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: 'Error Rate',
      value: formatPercent(kpi?.error_rate ?? null),
      icon: Zap,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
  ];

  return (
    <div className="grid grid-cols-4 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${card.bg}`}>
                <Icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className="text-xl font-semibold text-gray-900">{card.value}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendChart({ trend }: { trend: OverviewResponse['trend'] | undefined }) {
  const data = useMemo(() => {
    if (!trend) return [];
    return trend.map((p) => ({
      ...p,
      time: formatTime(p.timestamp),
    }));
  }, [trend]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Latency Trend</h3>
        <div className="h-64 flex items-center justify-center text-gray-400">No data available</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-sm font-medium text-gray-700 mb-4">Latency Trend</h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="p50Grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="p95Grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="time" tick={{ fontSize: 12, fill: '#9ca3af' }} />
          <YAxis
            tick={{ fontSize: 12, fill: '#9ca3af' }}
            tickFormatter={(v) => `${v}ms`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: 13,
            }}
            formatter={(value, name) => [
              value != null ? `${Math.round(Number(value))}ms` : '-',
              name === 'p50_ms' ? 'P50' : 'P95',
            ]}
            labelFormatter={(label) => label}
          />
          <Area
            type="monotone"
            dataKey="p50_ms"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#p50Grad)"
            name="p50_ms"
            connectNulls
          />
          <Area
            type="monotone"
            dataKey="p95_ms"
            stroke="#ef4444"
            strokeWidth={2}
            fill="url(#p95Grad)"
            name="p95_ms"
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function StageBar({ trace }: { trace: RequestListItem }) {
  const total = trace.total_duration_ms || 1;
  const segments = buildPerformanceStageSegments(trace);

  if (!segments.length) {
    return <div className="text-sm text-gray-400">No stage data</div>;
  }

  return (
    <div className="space-y-2">
      <div className="flex h-8 rounded-md overflow-hidden border border-gray-200">
        {segments.map((seg) => {
          const pct = ((seg.ms || 0) / total) * 100;
          return (
            <div
              key={seg.label}
              className="relative flex items-center justify-center text-xs text-white font-medium"
              style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: seg.color }}
              title={`${seg.label}: ${formatMs(seg.ms)}`}
            >
              {pct > 10 && <span>{formatMs(seg.ms)}</span>}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 text-xs text-gray-500">
        {segments.map((seg) => (
          <span key={seg.label} className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: seg.color }} />
            {seg.label}: {formatMs(seg.ms)}
          </span>
        ))}
      </div>
    </div>
  );
}

function TraceDetail({ traceId }: { traceId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['performance-detail', traceId],
    queryFn: () => performanceApi.getRequestDetail(traceId),
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading detail...</div>;
  }

  if (!data) {
    return <div className="p-4 text-sm text-gray-400">No detail available</div>;
  }

  const detail: TraceDetailResponse = data;
  const memoryDecisions = detail.memory_decisions ?? [];
  const visibleWaitMs = visibleContentWaitMs(detail.trace);

  return (
    <div className="p-4 space-y-4 bg-gray-50 border-t border-gray-200">
      {detail.error_message && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <div className="text-xs font-medium text-red-700">Request error</div>
          <pre className="mt-1 whitespace-pre-wrap break-words text-xs text-red-800">
            {detail.error_message}
          </pre>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          ['SSE First Byte', detail.trace.first_byte_ms],
          ['First Visible Content', detail.trace.first_content_ms],
          ['Visible Content Wait', visibleWaitMs],
          ['Total Request', detail.trace.total_duration_ms],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded-md border border-gray-200 bg-white px-3 py-2">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="mt-1 text-sm font-semibold text-gray-800">{formatMs(value as number | null)}</div>
          </div>
        ))}
      </div>
      <StageBar trace={detail.trace} />

      <MemoryDecisionRows decisions={memoryDecisions} />

      {detail.rounds.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Rounds</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="pb-1 pr-4">#</th>
                <th className="pb-1 pr-4">TTFB</th>
                <th className="pb-1 pr-4">Duration</th>
                <th className="pb-1 pr-4">Input Tokens</th>
                <th className="pb-1 pr-4">Output Tokens</th>
                <th className="pb-1 pr-4">Cache Read</th>
                <th className="pb-1 pr-4">Cache Create</th>
                <th className="pb-1">Tools</th>
              </tr>
            </thead>
            <tbody>
              {detail.rounds.map((r) => (
                <tr key={r.round_index} className="border-b border-gray-100">
                  <td className="py-1.5 pr-4 font-mono text-gray-600">{r.round_index}</td>
                  <td className="py-1.5 pr-4">{formatMs(r.ttfb_ms)}</td>
                  <td className="py-1.5 pr-4">{formatMs(r.duration_ms)}</td>
                  <td className="py-1.5 pr-4 text-gray-600">{r.input_tokens?.toLocaleString() ?? '-'}</td>
                  <td className="py-1.5 pr-4 text-gray-600">{r.output_tokens?.toLocaleString() ?? '-'}</td>
                  <td className="py-1.5 pr-4 text-gray-600">{r.cache_read_tokens?.toLocaleString() ?? '-'}</td>
                  <td className="py-1.5 pr-4 text-gray-600">{r.cache_creation_tokens?.toLocaleString() ?? '-'}</td>
                  <td className="py-1.5 text-gray-600">{r.tool_calls_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.tool_calls.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Tool Calls</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="pb-1 pr-4">Tool</th>
                <th className="pb-1 pr-4">Duration</th>
                <th className="pb-1 pr-4">Round</th>
                <th className="pb-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.tool_calls.map((tc, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="py-1.5 pr-4 font-mono text-gray-700">{tc.tool_name}</td>
                  <td className="py-1.5 pr-4">{formatMs(tc.duration_ms)}</td>
                  <td className="py-1.5 pr-4 text-gray-600">{tc.round_index ?? '-'}</td>
                  <td className="py-1.5">
                    {tc.is_error ? (
                      <span className="text-red-600 font-medium">Error</span>
                    ) : (
                      <span className="text-green-600">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.trace.opik_trace_id && (
        <a
          href={`https://www.comet.com/opik/trace/${detail.trace.opik_trace_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View in Opik
        </a>
      )}
    </div>
  );
}

export function PerformancePage() {
  const [range, setRange] = useState<TimeRange>('24h');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const pageSize = 20;

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['performance-overview', range],
    queryFn: () => performanceApi.getOverview(range),
  });

  const { data: requests, isLoading: requestsLoading } = useQuery({
    queryKey: ['performance-requests', range, page],
    queryFn: () =>
      performanceApi.listRequests({
        range,
        sort: 'total_duration_ms',
        order: 'desc',
        page,
        size: pageSize,
      }),
  });

  const totalPages = requests ? Math.ceil(requests.total / pageSize) : 0;

  // Batch fetch user display info
  const userIds = useMemo(
    () => [...new Set((requests?.items ?? []).map((r) => r.user_id))],
    [requests],
  );
  const { users: userInfoMap } = useUsersBatch(userIds, 'basic');

  const formatUser = (userId: string) => {
    const info = userInfoMap[userId];
    if (!info) return { name: userId.slice(0, 8) + '...', sub: '' };
    return {
      name: info.nickname || info.username || userId.slice(0, 8),
      sub: `@${info.username}`,
    };
  };

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-sm text-gray-500 mt-1">Request tracing and latency analysis</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.value}
              onClick={() => {
                setRange(tr.value);
                setPage(1);
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                range === tr.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      {overviewLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 h-20 animate-pulse" />
          ))}
        </div>
      ) : (
        <KPICards kpi={overview?.kpi} />
      )}

      <TrendChart trend={overview?.trend} />

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">
            Requests
            {requests && (
              <span className="ml-2 text-gray-400 font-normal">({requests.total} total)</span>
            )}
          </h3>
        </div>

        {requestsLoading ? (
          <div className="p-8 text-center text-gray-400">Loading requests...</div>
        ) : !requests?.items.length ? (
          <div className="p-8 text-center text-gray-400">No requests found</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2 w-8" />
                  <th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Agent</th>
                  <th className="px-4 py-2">Skill</th>
                  <th className="px-4 py-2 text-right">Total ms</th>
                  <th className="px-4 py-2 text-center">Rounds</th>
                  <th className="px-4 py-2 text-center">Tools</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.items.map((req) => {
                  const isExpanded = expandedRow === req.id;
                  const user = formatUser(req.user_id);
                  return (
                    <tr key={req.id} className="group">
                      <td
                        colSpan={9}
                        className="p-0"
                      >
                        <table className="w-full">
                          <tbody>
                            <tr
                              className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                                isExpanded ? 'bg-gray-50' : ''
                              }`}
                              onClick={() => setExpandedRow(isExpanded ? null : req.id)}
                            >
                              <td className="px-4 py-2.5 w-8">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                                {formatDateTime(req.created_at)}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="truncate max-w-[120px]">
                                  <span className="text-gray-900 text-sm">{user.name}</span>
                                  {user.sub && (
                                    <span className="text-gray-400 text-xs ml-1">{user.sub}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                  {req.agent_type}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {req.skill_matched || '-'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono">
                                <span
                                  className={
                                    (req.total_duration_ms ?? 0) > 10000
                                      ? 'text-red-600 font-semibold'
                                      : (req.total_duration_ms ?? 0) > 5000
                                      ? 'text-amber-600'
                                      : 'text-gray-700'
                                  }
                                >
                                  {formatMs(req.total_duration_ms)}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-center text-gray-600">
                                {req.total_rounds}
                              </td>
                              <td className="px-4 py-2.5 text-center text-gray-600">
                                {req.total_tool_calls}
                              </td>
                              <td className="px-4 py-2.5">
                                {req.finish_reason === 'error' ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-700">
                                    Error
                                  </span>
                                ) : req.is_quick_path ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                                    Quick
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700">
                                    OK
                                  </span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        {isExpanded && <TraceDetail traceId={req.id} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
